<script lang="ts">
  import Button from "$lib/Button.svelte";
  import params from "$lib/sessionValues";
  import { onMount } from "svelte";

  // Recursivly populates return `record` object with `keys`' values from `vals` object
  const getRecordFromKeysAndRecord = (
    keys: string[] | null,
    vals: Record<string, string>,
    record = {}
  ): Record<string, string> =>
    keys?.length
      ? getRecordFromKeysAndRecord(keys.slice(1), vals, {
          ...record,
          ...(vals[keys[0]] ? { [keys[0]]: vals[keys[0]] } : {}),
        })
      : record;

  // Keys for which session values to use, excluding:
  // - `source` (separate from typeformParams to not encode `#` in the href and for default value)

  interface Props {
    arrow?: boolean;
    primary?: boolean;
    target?: "_blank" | "_self" | "_parent" | "_top" | null;
    source?: string | undefined;
    lang?: string | undefined;
    // Typeform URL - can also include params, but better not
    url: string;
    // - `utm_source` (fallback value is "ishavskraft.no")
    sessionValuesKeys?: string[] | null;
    additionalValuesKeys?: string[];
    // Params to merge over the session values and props
    customParameters?: Record<string, string>;
    class?: string;
    children?: import("svelte").Snippet;
  }

  let {
    arrow = false,
    primary = false,
    target = "_blank",
    source = undefined,
    lang = undefined,
    url,
    sessionValuesKeys = [
      "at_gd", // adtraction id
      "mnr", // medlemsnr
      "mnr_type", // medlemsnr type
      "ClickID", // adservice click id
      "id", // adservice id
      "utm_id",
      "utm_medium",
      "utm_campaign",
      "utm_term",
      "utm_content",
      "campaign",
    ],
    additionalValuesKeys = [],
    customParameters = {},
    class: classes = "",
    children,
  }: Props = $props();

  let loading = $state(true);
  // `source` is separate from typeformParams to not encode `#` in the href and for default value
  let sourceForHref = $derived(encodeURIComponent(source || $params.source || "ishavskraft.no"));
  // Other URL params from sessionValues (TODO: is all params needed? Does it hurt?)
  let recordKeys = $derived(
    sessionValuesKeys ? [...sessionValuesKeys, ...additionalValuesKeys] : additionalValuesKeys
  );
  let typeformParams: Record<string, string> = $derived({
    ...{ utm_source: encodeURIComponent($params.utm_source || "ishavskraft.no") },
    ...getRecordFromKeysAndRecord(recordKeys, $params),
    ...customParameters,
  });

  // Handle URL with URL class
  let urlFromProp = $derived(new URL(url.startsWith("//") ? "https:" + url : url));
  let urlStrPreSearch = $derived(urlFromProp.origin + urlFromProp.pathname);
  let urlStrSearch = $derived.by(() => {
    const params = urlFromProp.searchParams;

    if (typeformParams)
      for (const key in typeformParams) {
        params.set(key, typeformParams[key]);
      }

    params.delete("source");

    const paramsStr = params.toString();

    return `?#source=${sourceForHref}${paramsStr ? `&${paramsStr}` : ""}`;
  });

  onMount(() => {
    setTimeout(() => {
      loading = false;
    }, 125);
  });
</script>

<Button
  class="typeform-button-component-button {classes}"
  {arrow}
  {target}
  {lang}
  {primary}
  disabled={loading}
  onclick={(e) => {
    e.preventDefault();
    const href = urlStrPreSearch + urlStrSearch;
    window.location.href = href;
  }}
>
  {@render children?.()}
</Button>

<style lang="postcss">
  :global(.typeform-button-component-button) {
    transition: background-color 0.25s var(--easing);
  }
</style>
